// Help (the Mac's Help window): the articles in Apps/Shared/Help, bundled when the app is built, listed by section
// with a search, one article at a time. `help:<id>` links open another article; a section describing something only
// the Apple devices do carries an "On your Mac or iPad" note (notes.ts). Reached at /help[/<article>], from each
// room page's ? and the Project menu; Back returns to the page it came from.
import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react';
import { Masthead } from '../shell/Gate.tsx';
import { followLink, pageBeforeHelp } from '../shell/route.ts';
import { linkTarget, parseBlocks, parseInline, type HelpBlock, type Inline } from './markdown.ts';
import { appleOnlyNote } from './notes.ts';
import { matching, sectionsOf, type HelpArticle } from './sections.ts';

declare const __HELP__: string | undefined;

let loading: Promise<Record<string, string>> | null = null;

/** The bundled articles (a hashed JSON asset, cached by the service worker), fetched once. */
export function loadArticles(): Promise<Record<string, string>> {
  const url = typeof __HELP__ === 'string' ? __HELP__ : null;
  if (!url) return Promise.resolve({});
  loading ??= fetch(url)
    .then((r) => {
      if (!r.ok) throw new Error(`Help didn’t load (${r.status}).`);
      return r.json() as Promise<Record<string, string>>;
    })
    .catch((e) => {
      loading = null;
      throw e;
    });
  return loading;
}

export const helpHref = (id: string) => `/help/${encodeURIComponent(id)}`;

export function HelpPage({ article }: { article: string | null }) {
  const [articles, setArticles] = useState<Record<string, string> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const heading = useRef<HTMLHeadingElement>(null);
  const [back] = useState(pageBeforeHelp);
  useEffect(() => {
    let live = true;
    loadArticles()
      .then((a) => live && setArticles(a))
      .catch((e) => live && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      live = false;
    };
  }, []);
  const sections = articles ? sectionsOf(articles) : [];
  const id = article && articles && article in articles ? article : (sections[0]?.topics[0]?.id ?? null);
  const topic: HelpArticle | null = id && articles ? sections.flatMap((s) => s.topics).find((t) => t.id === id) ?? null : null;
  useEffect(() => {
    if (!topic) return;
    document.title = `${topic.title} — Five Rooms Help`;
    heading.current?.focus({ preventScroll: true });
    window.scrollTo?.({ top: 0 });
  }, [topic?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="help-page" data-state="help" data-article={topic?.id ?? ''}>
      <Masthead>
        <a href={back} onClick={(e) => followLink(e, back)} data-help-back>
          {back === '/' ? 'Library' : 'Back'}
        </a>
      </Masthead>
      <div className="help-layout">
        <nav className="help-topics" aria-label="Help topics">
          <label className="column-search">
            <span className="visually-hidden">Search Help</span>
            <input type="search" placeholder="Search Help" value={query} onChange={(e) => setQuery(e.target.value)} />
          </label>
          {matching(sections, query).map((s) => (
            <section key={s.name}>
              <h2 className="eyebrow column-eyebrow">{s.name}</h2>
              <ul className="column-list">
                {s.topics.map((t) => (
                  <li key={t.id}>
                    <a className={`column-row${t.id === topic?.id ? ' selected' : ''}`} href={helpHref(t.id)} aria-current={t.id === topic?.id ? 'page' : undefined} onClick={(e) => followLink(e, helpHref(t.id))}>
                      <span className="column-row-title">{t.title}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </nav>
        <main className="help-article" id="page">
          {error ? (
            <p className="error" role="alert">
              {error}
            </p>
          ) : !articles ? (
            <p className="lede" aria-busy="true">
              Opening Help…
            </p>
          ) : topic ? (
            <Article topic={topic} headingRef={heading} missing={article !== null && article !== topic.id ? article : null} />
          ) : (
            <p className="lede">There’s no Help in this build.</p>
          )}
          <p className="help-legal">
            <a href="/privacy">Privacy</a>
            <a href="/terms">Terms of use</a>
          </p>
        </main>
      </div>
    </div>
  );
}

function Article({ topic, headingRef, missing }: { topic: HelpArticle; headingRef: React.RefObject<HTMLHeadingElement | null>; missing: string | null }) {
  const blocks = parseBlocks(topic.markdown);
  return (
    <article className="help-body" aria-labelledby="help-title">
      {missing ? <p className="notice">There’s no article called “{missing}”.</p> : null}
      {blocks.map((b, i) => (
        <Fragment key={i}>{renderBlock(b, topic.id, i === 0 ? headingRef : undefined)}</Fragment>
      ))}
    </article>
  );
}

function AppleNote({ article, heading }: { article: string; heading: string | null }) {
  const note = appleOnlyNote(article, heading);
  if (!note) return null;
  return (
    <aside className="apple-note" data-apple-note={heading ?? 'title'}>
      <strong>On your Mac or iPad:</strong> {note.text}
    </aside>
  );
}

function renderBlock(b: HelpBlock, article: string, headingRef?: React.RefObject<HTMLHeadingElement | null>): ReactNode {
  switch (b.type) {
    case 'title':
      return (
        <>
          <h1 id="help-title" ref={headingRef} tabIndex={-1}>
            {b.text}
          </h1>
          <AppleNote article={article} heading={null} />
        </>
      );
    case 'heading':
      return (
        <>
          <h2>{inline(parseInline(b.text))}</h2>
          <AppleNote article={article} heading={b.text} />
        </>
      );
    case 'subheading':
      return (
        <>
          <h3>{inline(parseInline(b.text))}</h3>
          <AppleNote article={article} heading={b.text} />
        </>
      );
    case 'paragraph':
      return <p>{inline(parseInline(b.text))}</p>;
    case 'bullets':
      return (
        <ul>
          {b.items.map((t, i) => (
            <li key={i}>{inline(parseInline(t))}</li>
          ))}
        </ul>
      );
    case 'numbered':
      return (
        <ol>
          {b.items.map((t, i) => (
            <li key={i}>{inline(parseInline(t))}</li>
          ))}
        </ol>
      );
    case 'note':
      return <p className="help-note">{inline(parseInline(b.text))}</p>;
    case 'table': {
      const [head, ...rows] = b.rows;
      return (
        <div className="table-wrap" role="region" aria-label="Table" tabIndex={0}>
          <table className="help-table">
            {head ? (
              <thead>
                <tr>
                  {head.map((c, i) => (
                    <th key={i} scope="col">
                      {inline(parseInline(c))}
                    </th>
                  ))}
                </tr>
              </thead>
            ) : null}
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  {r.map((c, j) => (
                    <td key={j}>{inline(parseInline(c))}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    case 'code':
      return (
        <pre className="help-code">
          <code>{b.text}</code>
        </pre>
      );
  }
}

function inline(nodes: Inline[]): ReactNode {
  return nodes.map((n, i) => {
    switch (n.type) {
      case 'text':
        return <Fragment key={i}>{n.text}</Fragment>;
      case 'strong':
        return <strong key={i}>{inline(n.children)}</strong>;
      case 'em':
        return <em key={i}>{inline(n.children)}</em>;
      case 'code':
        return <code key={i}>{n.text}</code>;
      case 'link': {
        const to = linkTarget(n.href);
        if (!to) return <Fragment key={i}>{inline(n.children)}</Fragment>;
        if (to.kind === 'article') {
          const href = helpHref(to.id);
          return (
            <a key={i} href={href} data-help-link={to.id} onClick={(e) => followLink(e, href)}>
              {inline(n.children)}
            </a>
          );
        }
        return (
          <a key={i} href={to.href} target="_blank" rel="noopener noreferrer">
            {inline(n.children)}
          </a>
        );
      }
    }
  });
}
